import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, bootPage, ApiError } from '../api';
import { money, nfmt } from '../format';
import { lbl } from '../labels';
import { Button } from '../components/Button';
import { FEEDBACK_WIDGET_EVENT } from '../shell/FeedbackWidget';
import { Select } from '../components/Select';
import { PanePageSize, PanePager } from '../components/PanePageSize';
import { Table } from '../components/Table';
import { AdminTabs } from './admin/AdminTabs';

/**
 * Admin — the overview tab from `/api/v1/admin/overview`.
 *
 * The EJS page renders these as label/value tables rather than cards (columns "Metric" and "Value"), and
 * that is what this mirrors: Accounts, System health, Backups and the users table. The other admin tabs
 * (feedback triage, metrics, jobs, tests) are not ported yet and are named as such.
 *
 * A non-admin never sees this page: the endpoint answers 404 for them, the same as a missing page.
 */
type FeedbackRow = {
  id: number;
  created_at: string;
  type: string;
  title: string;
  body?: string;
  status: string;
  page_context?: string | null;
  /** The join in api/pages.js names it user_email; rows posted by older code may carry email. */
  user_email?: string | null;
  email?: string | null;
  has_image?: number;
};

type Overview = {
  stats: {
    users: number;
    admins: number;
    newestUser: { email: string; created_at: string } | null;
    lists: number;
    transactions: number;
    dividends: number;
    lots: number;
    disposals: number;
    dbSize: number | null;
    walSize: number | null;
    lastPriceSync?: string;
    priceLogTickers?: number;
    nodeUptime?: number;
    nodeVersion?: string;
  };
  pf: {
    totalValue: number;
    totalCost: number;
    unrealized: number;
    realizedThisYear: number;
    dividendsAllTime: number;
    unpricedTickers: number;
    stalePrices: number;
    topTickers: { ticker: string; shares: number; value: number }[];
  };
  /** The admin's own roadmap document, shown verbatim — it is prose, not data. */
  assessment?: string | null;
  feedbackWidget?: string;
  users: {
    id: number;
    email: string;
    role: string;
    created: string;
    lastLogin: string | null;
    lastSeen: string | null;
    listCount: number;
    txCount: number;
    dividends: number;
    value: number;
  }[];
  backups: {
    heartbeatAgeH: number | null;
    state: string;
    offHost: { confirmed: boolean; ageH: number | null };
    generations: { daily: number | null; weekly: number | null; monthly: number | null };
    newestGenDate: string | null;
    retentionSource: string;
    nextRun: string | null;
    checkPassing: boolean;
  };
  feedback: {
    rows: FeedbackRow[];
    status: string;
    type: string;
    statuses: string[];
    types: string[];
    unread?: number;
    last_seen_at?: string;
  };
};

/** The EJS page's own abbreviation for an age: "45 min", "19 h". */
const ageTxt = (h: number | null) => (h == null ? '—' : h < 1 ? `${Math.round(h * 60)} min` : `${Math.round(h)} h`);
const ago = (h: number | null) => (h == null ? '—' : `${ageTxt(h)} ago`);

function MetricTable({
  id,
  heading,
  rows,
  style,
}: {
  id?: string;
  heading: string;
  rows: [string, React.ReactNode][];
  style?: React.CSSProperties;
}) {
  return (
    <section className="panel-card" id={id} style={style}>
      <h2>{heading}</h2>
      <Table
        expandable={false}
        wrap
        fit
        tableId={id ? `${id}-table` : undefined}
        tableClass=""
        columns={[{ label: lbl('Metric') }, { label: lbl('Value') }]}
        rows={rows.map(([k, v], i) => ({ key: i, cells: [k, v] }))}
      />
    </section>
  );
}

/** A stored UTC "YYYY-MM-DD HH:MM:SS" as the reader's local YYYY-MM-DD (admin.ejs's data-fmt="date"). */
function localDate(utc: string): string {
  const d = new Date(utc.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return utc.slice(0, 10);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

export function Admin() {
  // The feedback filters are the page's GET parameters (views/admin.ejs's own form), so they are read from the
  // URL rather than held in state.
  const fbQuery = new URLSearchParams(window.location.search);
  // The page's GET parameters, held in state so a change repaints without a document navigation (the EJS form
  // reloads the page; the rows it shows are the same ones).
  const [fbFilter, setFbFilter] = useState({ status: fbQuery.get('status') || '', type: fbQuery.get('type') || '' });
  const fbStatus = fbFilter.status;
  const fbType = fbFilter.type;
  const applyFbFilter = (next: { status: string; type: string }) => {
    setFbFilter(next);
    const params = new URLSearchParams();
    if (next.status) params.set('status', next.status);
    if (next.type) params.set('type', next.type);
    const qs = params.toString();
    window.history.replaceState(null, '', '/admin/overview' + (qs ? '?' + qs : ''));
  };
  const [openFb, setOpenFb] = useState<number | null>(null);
  // The users table's rows-per-page control: the EJS page paginates in the browser and shows the control in a
  // `.pane-pagebar`; without it the table is a wall of every account.
  // The size is the CONTROL's value ('5'…'100'|'all'); the table's rows-per-page is derived from it, so the
  // button can say "All 2 users" instead of inventing a page size that happens to equal the row count.
  // Remembered across reloads under the same kind of key the panes use: a reader who wants five users per page
  // wants it next time too.
  const [userPageSize, setUserPageSize] = useState<string>(() => {
    try {
      return localStorage.getItem('pf.usersPageSize') || '10';
    } catch {
      return '10';
    }
  });
  const setUserSize = (v: string) => {
    setUserPageSize(v);
    try {
      localStorage.setItem('pf.usersPageSize', v);
    } catch {
      /* private mode */
    }
  };
  const [page, setPage] = useState(0);
  // The launcher style: a site setting the buttons write. Declared HERE, above the early returns below, because
  // a hook declared after them runs on some renders and not others — React then renders nothing at all.
  const [widget, setWidgetState] = useState<string>('bubble');
  const setWidget = async (w: string) => {
    setWidgetState(w);
    const res = await fetch('/api/v1/admin/feedback-widget', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ widget: w }),
    });
    // Only claim the change once the server has it, and tell the SHELL as well: the launcher is drawn by the app
    // shell, not by this page, so the three buttons used to write the setting and leave the screen untouched.
    if (!res.ok) return;
    window.dispatchEvent(new CustomEvent(FEEDBACK_WIDGET_EVENT, { detail: w }));
    qc.invalidateQueries({ queryKey: ['admin-overview'] });
  };
  const qc = useQueryClient();
  const q = useQuery<Overview, ApiError>({
    queryKey: ['admin-overview'],
    initialData: bootPage<Overview>('admin-overview'),
    queryFn: () => apiGet<Overview>('/api/v1/admin/overview') });

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error?.status === 404) return <main className="panel-card">Not found.</main>;
  if (q.error) return <main className="panel-card">Could not load the admin overview: {q.error.message}</main>;

  const data = q.data!;
  // The payload is the truth once it arrives; the buttons' own state is what the reader just clicked.
  const shownWidget = widget === 'bubble' && data.feedbackWidget && data.feedbackWidget !== 'bubble' ? data.feedbackWidget : widget;
  const { stats, pf, users, backups, feedback: fb } = data;
  const pageSize = userPageSize === 'all' ? users.length : Number(userPageSize);
  // The triage's own state: the two filters, which row is open, and the status write.
  const fbRows = (fb.rows ?? []).filter(
    (f) => (!fbStatus || f.status === fbStatus) && (!fbType || f.type === fbType),
  );
  const isUnread = (f: FeedbackRow) => !!fb.unread && (!fb.last_seen_at || f.created_at > fb.last_seen_at);
  /**
   * A row's own select wrote a value: post the form the reader is looking at, exactly as views/admin.ejs's
   * delegated handler does (`/feedback/:id/type` and `/feedback/:id/status`, form-encoded), then re-read the
   * page so the row cannot disagree with the server.
   */
  const writeFbField = async (id: number, field: 'type' | 'status', value: string) => {
    await fetch(`/feedback/${id}/${field}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: new URLSearchParams({ [field]: value }),
    });
    qc.invalidateQueries({ queryKey: ['admin-overview'] });
  };
  // No wrapper element: views/admin.ejs writes the word's span and the age as the cell's own content.
  const backupState =
    backups.state === 'fresh' ? (
      <>
        <span className="pos">OK</span> — {ago(backups.heartbeatAgeH)}
      </>
    ) : backups.state === 'stale' ? (
      <>
        <span className="neg">STALE</span> — {ago(backups.heartbeatAgeH)}
      </>
    ) : (
      <span className="neg">never backed up</span>
    );

  return (
    <main>
      {/* The bar is shared by every admin page (EJS: partials/admin-tabs.ejs). It goes first, with the page's
          own lead under it, because on the EJS pages the bar is the first thing in `main`. */}
      <AdminTabs active="overview" />
      {/* views/admin.ejs wraps the whole tab in #admin-tab-overview.admin-tab-panel; the page's own stylesheet
          (and the scanner's component matching) address the panel, not the individual cards. */}
      <div id="admin-tab-overview" className="admin-tab-panel">
      <p className="muted t-body">Site-wide statistics — admin only.</p>

      <div className="admin-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <MetricTable
          style={{ marginTop: 0 }}
          heading="Accounts"
          rows={[
            ['Total users', stats.users],
            ['Admins', stats.admins],
            ['Newest user', stats.newestUser ? `${stats.newestUser.email} (${stats.newestUser.created_at.slice(0, 10)})` : '—'],
            ['Lists', stats.lists],
            ['Transactions', nfmt(stats.transactions)],
            ['Dividend rows', nfmt(stats.dividends)],
            ['Lots', nfmt(stats.lots)],
            ['Disposals', nfmt(stats.disposals)],
          ]}
        />
        <MetricTable
          style={{ marginTop: 0 }}
          heading="System health"
          rows={[
            ['DB size', stats.dbSize != null ? `${nfmt(Math.round(stats.dbSize / 1024))} KB` : '—'],
            ['WAL size', stats.walSize != null ? `${nfmt(Math.round(stats.walSize / 1024))} KB` : '—'],
            ['Last price sync', stats.lastPriceSync || '—'],
            // views/admin.ejs's System health names seven facts; these two were missing.
            ['Tickers in price log', stats.priceLogTickers],
            // The page's own order, and its own markup: a count is plain text unless it is non-zero, in which
            // case it is wrapped in `span.neg`. A span around a zero is markup the reference never writes.
            pf.unpricedTickers ? (
              ['Holdings with no price', <span key="u" className="neg">{pf.unpricedTickers}</span>]
            ) : (
              ['Holdings with no price', pf.unpricedTickers]
            ),
            pf.stalePrices ? (
              ['Prices older than 7 days', <span key="s" className="neg">{pf.stalePrices}</span>]
            ) : (
              ['Prices older than 7 days', pf.stalePrices]
            ),
            [
              'Node uptime',
              stats.nodeUptime == null
                ? '—'
                : `${Math.floor(stats.nodeUptime / 3600)}h ${Math.floor((stats.nodeUptime % 3600) / 60)}m`,
            ],
            ['Node version', stats.nodeVersion],
          ]}
        />
      </div>

      {/* The EJS table names five facts, so this one does too — the SPA had invented a longer list of
          differently-named backup rows (Generations, Newest generation, Retention source, Check), which the
          numbers check caught: the same page describing a different backup. */}
      <MetricTable
        heading="Backups"
        rows={[
          ['Last backup', backupState],
          ['Next scheduled run', backups.nextRun ? new Date(backups.nextRun).toLocaleString() : '—'],
          [
            'Retention on share',
            backups.generations.daily == null && backups.generations.weekly == null && backups.generations.monthly == null
              ? '—'
              : `${backups.generations.daily == null ? '—' : nfmt(backups.generations.daily)} daily / ` +
                `${backups.generations.weekly == null ? '—' : nfmt(backups.generations.weekly)} weekly / ` +
                `${backups.generations.monthly == null ? '—' : nfmt(backups.generations.monthly)} monthly` +
                (backups.newestGenDate
                  ? `, newest ${backups.newestGenDate.slice(0, 4)}-${backups.newestGenDate.slice(4, 6)}-${backups.newestGenDate.slice(6, 8)}`
                  : ''),
          ],
          ['Off-host upload', backups.offHost ? <span key="o" className="pos">confirmed</span> : '—'],
          ['Nightly check', backups.checkPassing === true ? <span key="c" className="pos">passing</span> : backups.checkPassing === false ? <span key="c" className="neg">failing</span> : '—'],
        ]}
      />

      {/* The EJS page puts the totals and the ticker table side by side; the second card is short, so a
          single column would leave a tall empty panel beside it. */}
      <div className="admin-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <section className="panel-card">
          <h2>Portfolio totals (all users)</h2>
          <Table
            expandable={false}
            wrap
            fit
            tableClass=""
            columns={[{ label: lbl('Metric') }, { label: lbl('Value'), num: true }]}
            rows={[
              // The EJS page writes these labels as plain text: `lbl()` renders a full/short span pair, and a
              // table cell is not a `th` or `.card .label`, so BOTH spans paint — the row read
              // "Unrealizedunreal". The same helper inside a template literal stringified to "[object Object]"
              // for the Realized row's key.
              { key: 'v', cells: ['Total market value', money(pf.totalValue)] },
              { key: 'c', cells: ['Total cost basis', money(pf.totalCost)] },
              {
                key: 'u',
                cells: ['Unrealized', <span key="u2" className={pf.unrealized >= 0 ? 'pos' : 'neg'}>{money(pf.unrealized)}</span>],
              },
              {
                key: 'r',
                cells: [
                  `Realized (${new Date().getFullYear()})`,
                  <span key="r2" className={pf.realizedThisYear >= 0 ? 'pos' : 'neg'}>{money(pf.realizedThisYear)}</span>,
                ],
              },
              { key: 'd', cells: ['Dividends all time', money(pf.dividendsAllTime)] },
            ]}
          />
        </section>
        <section className="panel-card">
          <h2>Top tickers by market value</h2>
          <Table
            expandable={false}
            wrap
            fit
            tableClass=""
            tableId="top-tickers"
            columns={[
              { label: lbl('Ticker') },
              { label: lbl('Shares (all users)'), num: true },
              { label: lbl('Market value'), num: true },
            ]}
            rows={pf.topTickers.map((t) => ({ key: t.ticker, cells: [t.ticker, nfmt(t.shares), money(t.value)] }))}
            empty="No holdings yet."
          />
        </section>
      </div>

      <section className="panel-card">
        <h2>Users</h2>
        <Table
          expandable={false}
          tableClass=""
          tableId="users-table"
          columns={[
            { label: lbl('ID') },
            { label: lbl('Email') },
            { label: lbl('Role') },
            { label: lbl('Lists'), num: true },
            { label: lbl('Transactions'), num: true },
            { label: lbl('Holdings value'), num: true },
            { label: lbl('Dividends'), num: true },
            { label: lbl('Registered') },
            { label: lbl('Last login') },
            { label: lbl('Last seen') },
          ]}
          rows={users.slice(page * pageSize, page * pageSize + pageSize).map((u) => ({
            key: u.id,
            cells: [
              // The id, the two timestamps and the role's ink are the page's own: a muted cell is part of the
              // table's look, and the admin role's tag is accent-coloured in views/admin.ejs.
              { content: u.id, className: 'muted' },
              <span key="e" className="user-email">
                {u.email}
              </span>,
              u.role === 'admin' ? (
                <span key="r" className="tag" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}>
                  {u.role.toUpperCase()}
                </span>
              ) : (
                <span key="r" className="tag">{u.role.toUpperCase()}</span>
              ),
              u.listCount,
              u.txCount,
              // Abbreviated, with the exact figure in the tooltip: a users table is a scan, and "$2.0K" is
              // what the EJS page shows for the same number.
              <span key="v" title={money(u.value)}>{abbrM(u.value)}</span>,
              <span key="d" title={money(u.dividends)}>{abbrM(u.dividends)}</span>,
              // Local date, not the stored UTC slice: the DB writes CURRENT_TIMESTAMP (UTC), and after 17:00
              // local the raw string is already tomorrow's date. views/admin.ejs rewrites those <time> nodes
              // with the same conversion on load; slicing here showed a different day from the EJS page.
              {
                className: 'muted',
                content: u.created ? (
                  <time dateTime={u.created.replace(' ', 'T') + 'Z'} data-utc={u.created} title={u.created + ' UTC'}>
                    {localDate(u.created)}
                  </time>
                ) : (
                  '—'
                ),
              },
              // Local time for reading, the stored UTC in the tooltip: the same instant, two audiences. The
              // database keeps UTC; the admin reading the table is in their own zone.
              {
                className: 'muted',
                content: u.lastLogin ? (
                // A <time> element, as the page uses: the visible text is local, `data-utc` and the tooltip keep
                // the stored value, so the reader in Tokyo and the tooltip in UTC are both right.
                <time
                  dateTime={u.lastLogin.replace(' ', 'T') + 'Z'}
                  data-utc={u.lastLogin}
                  title={u.lastLogin}
                >
                  {(() => {
                    // YYYY-MM-DD HH:MM in the reader's zone: a sortable, unambiguous shape, which the locale's
                    // own "2/1/2026, 8:30:00 AM" is not.
                    const d = new Date(u.lastLogin.replace(' ', 'T') + 'Z');
                    const p2 = (n: number) => String(n).padStart(2, '0');
                    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
                  })()}
                </time>
              ) : (
                '—'
              ),
              },
              // Last seen is the device log's own fact — a different column from last login, because a reader
              // can be signed in for days without signing in again.
              {
                className: 'muted',
                content: u.lastSeen ? (
                <time
                  key="seen"
                  dateTime={u.lastSeen.replace(' ', 'T') + 'Z'}
                  data-utc={u.lastSeen}
                  title={u.lastSeen}
                >
                  {(() => {
                    const d = new Date(u.lastSeen.replace(' ', 'T') + 'Z');
                    const p2 = (n: number) => String(n).padStart(2, '0');
                    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
                  })()}
                </time>
              ) : (
                <span key="seen" className="muted">never</span>
              ),
              },
            ],
          }))}
        />
        {/* views/admin.ejs's pagebar carries the base .pane-pagebar margins (8px above, 4px below); #root's own
            rule narrows that to 0/8 for the ticker pane, so this one states it. */}
        <div className="pane-pagebar" style={{ margin: '8px 0 4px' }}>
          <div className="pane-pagesize">
            <span className="muted t-small">Show</span>
            <PanePageSize id="users-pagesize-btn" value={userPageSize} onChange={(v) => { setUserSize(v); setPage(0); }} noun="users" total={users.length} />
          </div>
          <PanePager countInk="inline" id="users-pager" total={users.length} size={pageSize} page={page + 1} onPage={(p) => setPage(p - 1)} noun="users" all={userPageSize === 'all'} />
        </div>
      </section>



      {/* Feedback triage, ported row for row from views/admin.ejs + views/partials/feedback-rows.ejs: a native
          select per row inside its own form (the type and the status are the row's own display of the value),
          a muted date/page/email column, the partial's own chevron column, and a detail row held in the DOM
          hidden — clicking the row swaps which one is open, exactly as the page's script does. */}
      <section className="panel-card" style={{ marginTop: 24 }}>
        <h2>Feedback</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, margin: '0 0 8px', flexWrap: 'wrap' }}>
          <span className="muted t-body">
            {fb.unread ? (
              <>
                Bugs and feature requests submitted by users{' — '}
                <strong>{fb.unread} unread</strong>.
              </>
            ) : (
              'Bugs and feature requests submitted by users.'
            )}
          </span>
          <form
            method="get"
            action="/admin/overview"
            id="feedback-filter-form"
            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
            // The EJS form SUBMITS — it is a server-rendered page, so a GET is a reload that renders the filtered
            // rows. In the SPA that submit is a document navigation: the whole app boots again to show a filter
            // the page had already applied, losing scroll position, the open row and an unsaved edit. The selects
            // already filter in place through applyFbFilter; the button does the same, and only the URL follows.
            onSubmit={(e) => {
              e.preventDefault();
              applyFbFilter(fbFilter);
            }}
          >
            {/* The EJS page's selects are enhanced into .sel dropdowns by the layout script, which is what the
                SPA's Select renders; onChange filters immediately, as the GET reload does there. */}
            <Select
              id="feedback-status-filter"
              name="status"
              value={fbStatus}
              ariaLabel="Filter by status"
              options={[{ value: '', label: 'All statuses' }, ...(fb.statuses ?? []).map((st) => ({ value: st, label: st }))]}
              onChange={(v) => applyFbFilter({ ...fbFilter, status: v })}
            />
            <Select
              id="feedback-type-filter"
              name="type"
              value={fbType}
              ariaLabel="Filter by type"
              options={[{ value: '', label: 'All types' }, ...(fb.types ?? []).map((t) => ({ value: t, label: t }))]}
              onChange={(v) => applyFbFilter({ ...fbFilter, type: v })}
            />
            <Button variant="ghost" type="submit">
              {lbl('Filter')}
            </Button>
          </form>
        </div>
        <div className="table-scroll">
          <table
            id="feedback-table"
            className="data-table holdings-table row-hover"
            style={{ width: '100%', minWidth: 0 }}
          >
            <thead>
              <tr>
                <th>{lbl('Created')}</th>
                <th>{lbl('Type')}</th>
                <th>{lbl('Title')}</th>
                <th>{lbl('Status')}</th>
                <th>{lbl('Page')}</th>
                <th>{lbl('Email')}</th>
                <th style={{ width: 20 }} />
              </tr>
            </thead>
            <tbody>
              {fbRows.map((f) => {
                const open = openFb === f.id;
                const unread = isUnread(f);
                return (
                  <Fragment key={f.id}>
                    <tr
                      className={'fb-row row-action' + (unread ? ' fb-unread' : '') + (open ? ' expanded' : '')}
                      tabIndex={0}
                      role="button"
                      aria-label="Feedback — expand details"
                      data-fb-row={f.id}
                      style={unread ? { fontWeight: 600 } : undefined}
                      onClick={() => setOpenFb(open ? null : f.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenFb(open ? null : f.id);
                        }
                      }}
                    >
                      <td className="muted">{f.created_at.slice(0, 10)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <form
                          method="post"
                          action={`/feedback/${f.id}/type`}
                          className="feedback-type-form"
                          style={{ display: 'inline' }}
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <Select
                            name="type"
                            value={f.type}
                            ariaLabel={`Type of ${f.title}`}
                            options={(fb.types ?? []).map((t) => ({ value: t, label: t }))}
                            onChange={(v) => void writeFbField(f.id, 'type', v)}
                          />
                        </form>
                      </td>
                      <td title={f.title}>
                        {f.has_image ? (
                          <span
                            title="Has a screenshot attached"
                            style={{ display: 'inline-flex', verticalAlign: -2, marginRight: 5, color: 'var(--muted)' }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ display: 'block' }}
                            >
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                          </span>
                        ) : null}
                        {f.title.length > 60 ? f.title.slice(0, 60) + '…' : f.title}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <form
                          method="post"
                          action={`/feedback/${f.id}/status`}
                          className="feedback-status-form"
                          data-id={f.id}
                          style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <Select
                            name="status"
                            value={f.status}
                            ariaLabel={`Status of ${f.title}`}
                            options={(fb.statuses ?? []).map((s) => ({ value: s, label: s }))}
                            onChange={(v) => void writeFbField(f.id, 'status', v)}
                          />
                        </form>
                      </td>
                      <td className="muted">{f.page_context || '—'}</td>
                      <td className="muted">{f.user_email ?? f.email}</td>
                      <td style={{ paddingLeft: 14, position: 'relative' }}>
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            right: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 8,
                            paddingRight: 10,
                          }}
                        >
                          <span className="chev" style={{ display: 'inline-flex' }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
                              <path
                                d="M2 4 L6 8 L10 4"
                                fill="none"
                                stroke="var(--muted)"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* The detail row is markup at rest — the partial renders it on every row with display:none
                        and the script swaps which one is open. */}
                    <tr className="fb-detail detail-row" data-detail-for={f.id} style={open ? undefined : { display: 'none' }}>
                      <td colSpan={7} style={{ background: 'rgba(0,0,0,0.15)', padding: '12px 14px' }}>
                        <div className="t-small" style={{ color: 'var(--muted)', margin: '0 0 8px' }}>
                          Page: {f.page_context || '—'} · Submitted by {f.user_email ?? f.email} · {f.created_at}
                        </div>
                        <pre
                          className="t-body"
                          style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: 'var(--text)',
                            fontFamily: 'inherit',
                          }}
                        >
                          {f.body}
                        </pre>
                        {f.has_image ? (
                          <div style={{ marginTop: 10 }}>
                            <a href={`/feedback/${f.id}/image`} target="_blank" rel="noopener" title="Open full size">
                              <img
                                src={`/feedback/${f.id}/image`}
                                alt="Screenshot attached to this feedback"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: 420,
                                  border: '1px solid var(--border)',
                                  borderRadius: 6,
                                  display: 'block',
                                }}
                              />
                            </a>
                            <div className="muted t-small" style={{ marginTop: 4 }}>
                              Screenshot — click to open full size
                            </div>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
              {!fbRows.length ? (
                <tr>
                  <td colSpan={7} className="muted">
                    No feedback{fbStatus || fbType ? ' matching the filter' : ' yet'}.
                    {fbStatus || fbType ? (
                      <>
                        {' '}
                        <a href="/admin/overview" style={{ color: 'var(--accent)' }}>
                          Clear filter
                        </a>{' '}
                        — items are only reclassifiable from the unfiltered list.
                      </>
                    ) : null}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel-card" style={{ marginTop: 24 }}>
        <h2>Global settings</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="t-body" style={{ fontWeight: 600, marginBottom: 2 }}>
              Send-feedback launcher
            </div>
            <div className="muted t-small">
              Fixed button shown on every page for all users: a bubble at the bottom-right, a tab on the left
              edge, or none (the user-menu entry always works).
            </div>
          </div>
          {/* One segment per choice, square-joined inside a single rounded box: the group owns the rounding. */}
          <span
            id="fb-widget-picker"
            style={{
              display: 'flex',
              gap: 0,
              border: '1px solid var(--border)',
              borderRadius: 7,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {[
              ['bubble', 'Bubble'],
              ['tab', 'Edge tab'],
              ['none', 'Hidden'],
            ].map(([value, label], i) => (
              <Button
                key={value}
                variant="quiet"
                className="t-small seg-btn"
                attrs={{ 'data-fb-widget': value, 'aria-pressed': shownWidget === value }}
                onClick={() => void setWidget(value)}
                style={{
                  borderLeft: i ? '1px solid var(--border)' : 'none',
                  background: shownWidget === value ? 'var(--accent-tint-strong)' : 'none',
                  color: shownWidget === value ? 'var(--text)' : 'var(--muted)',
                  // The page's own paint(): the chosen segment is bold, the others are not.
                  fontWeight: shownWidget === value ? 600 : 400,
                }}
              >
                {label}
              </Button>
            ))}
          </span>
        </div>
      </section>

      <section className="panel-card" style={{ marginTop: 24 }}>
        <h2>Remaining work / roadmap</h2>
        {data.assessment ? (
          <pre
            style={{
              margin: 0,
              lineHeight: 1.55,
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          >
            {data.assessment}
          </pre>
        ) : (
          <p className="muted t-small">claude-assessment.txt not found.</p>
        )}
      </section>
      </div>
    </main>
  );
}

/** $2,000 → $2.0K: the same abbreviation the EJS pages use for a figure read at a glance. */
function abbrM(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
